// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsAuthorityGetAllAttrs,
  IAboutUsAuthorityGetAttrs,
  IAboutUsAuthoritySaveAttrs,
} from 'interfaces/back-office/content-management/about-us/authority/about-us-authority-view.interface'
import {
  IAboutUsAuthorityApiGetAllResponse,
  IAboutUsAuthorityApiGetResponse,
  IAboutUsAuthorityApiPaginatedResponse,
  IAboutUsAuthorityApiSaveResponse,
} from 'interfaces/back-office/content-management/about-us/authority/about-us-authority-api.interface'
import { IAboutUsAuthorityDetail } from 'interfaces/back-office/content-management/about-us/authority'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// About Us Authority Props
const aboutUsAuthorityProps = {
  id: types.maybeNull(types.number),
  number: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// About Us Authority List Model
const AboutUsAuthorities = types
  .model('AboutUsAuthorities')
  .props(withHelper_baseResponsePaginationTypes(types.model('AboutUsAuthorityList').props(aboutUsAuthorityProps)))

// About Us Authority Detail Model
const AboutUsAuthorityDetail = types.model('AboutUsAuthorityDetail').props(aboutUsAuthorityProps)

const AboutUsAuthorityStoreModel = types
  .model('AboutUsAuthority')
  .props({
    aboutUsAuthority_isLoading: types.optional(types.boolean, false),
    aboutUsAuthority_isSaveLoading: types.optional(types.boolean, false),
    aboutUsAuthority_aboutUsAuthorities: types.optional(AboutUsAuthorities, {}),
    aboutUsAuthority_detail: types.optional(AboutUsAuthorityDetail, {}),
  })
  .actions(self => ({
    aboutUsAuthority_SET_LOADING: (payload: boolean): void => {
      self.aboutUsAuthority_isLoading = payload
    },
    aboutUsAuthority_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsAuthority_isSaveLoading = payload
    },
    aboutUsAuthority_SET_ABOUT_US_AUTHORITIES: (payload: IAboutUsAuthorityApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.aboutUsAuthority_aboutUsAuthorities = cast(mapPayload)
    },
    aboutUsAuthority_SET_ABOUT_US_AUTHORITY: (payload: IAboutUsAuthorityDetail): void => {
      self.aboutUsAuthority_detail = cast(payload)
    },
    aboutUsAuthority_CLEAR_ABOUT_US_AUTHORITIES: (): void => {
      self.aboutUsAuthority_aboutUsAuthorities = cast({})
    },
    aboutUsAuthority_CLEAR_ABOUT_US_AUTHORITY: (): void => {
      self.aboutUsAuthority_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about Us Authorities
     *
     * @param {IAboutUsAuthorityGetAllAttrs} payload
     *
     * @return {any} any
     */
    aboutUsAuthority_fetchAboutUsAuthorities: flow(function* (payload: IAboutUsAuthorityGetAllAttrs) {
      self.aboutUsAuthority_SET_LOADING(true)

      const response: IAboutUsAuthorityApiGetAllResponse =
        yield self.environment.aboutUsAuthorityApi.aboutUsAuthority_getAboutUsAuthorities({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) self.aboutUsAuthority_SET_ABOUT_US_AUTHORITIES(response.res.data)

      self.aboutUsAuthority_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us authority
     *
     * @param {IAboutUsAuthorityGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsAuthority_fetchAboutUsAuthority: flow(function* (payload: IAboutUsAuthorityGetAttrs) {
      self.aboutUsAuthority_SET_LOADING(true)

      const response: IAboutUsAuthorityApiGetResponse =
        yield self.environment.aboutUsAuthorityApi.aboutUsAuthority_getAboutUsAuthority({
          params: { id: payload.params.id },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.aboutUsAuthority_SET_ABOUT_US_AUTHORITY(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsAuthority_SET_LOADING(false)
    }),

    /**
     * @description Save about us authority
     *
     * @param {IAboutUsAuthoritySaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsAuthority_saveAboutUsAuthority: flow(function* (payload: IAboutUsAuthoritySaveAttrs) {
      self.aboutUsAuthority_SET_SAVE_LOADING(true)

      const response: IAboutUsAuthorityApiSaveResponse =
        yield self.environment.aboutUsAuthorityApi.aboutUsAuthority_saveAboutUsAuthority({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsAuthority_SET_SAVE_LOADING(false)
    }),
  }))

export const createAboutUsAuthorityStoreDefaultModel = () => types.optional(AboutUsAuthorityStoreModel, {})
